import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import Input from '../../../components/ui/Input'
import SearchableSelect from '../../../components/ui/SearchableSelect'
import TypeaheadSearch, { TypeaheadOption } from '../../../components/ui/TypeaheadSearch'
import Badge from '../../../components/ui/Badge'
import Table, { Pagination } from '../../../components/ui/Table'
import Modal from '../../../components/ui/Modal'
import api from '../../../lib/api'
import { formatDate } from '../../../lib/formatters'

interface User {
  id: string
  email: string
  phone?: string
  name: string
  role: string
  is_active: boolean
  last_login_at?: string
  created_at: string
}

interface PaginatedResponse<T> {
  data: T[]
  meta: {
    page: number
    per_page: number
    total: number
    total_pages: number
  }
}

const ROLES = [
  { value: 'admin', label: 'Admin', description: 'Full access including user management' },
  { value: 'operator', label: 'Operator', description: 'Can manage everything except users' },
]

export default function UsersListPage() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['users', page, search],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), per_page: '10' })
      if (search) params.append('search', search)
      const { data } = await api.get<PaginatedResponse<User>>(`/users?${params}`)
      return data
    },
  })

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      return api.put(`/users/${id}`, { is_active })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  const getRoleBadge = (role: string) => {
    const variants: Record<string, 'error' | 'warning' | 'info' | 'success'> = {
      admin: 'error',
      operator: 'info',
    }
    return <Badge variant={variants[role] || 'info'}>{role}</Badge>
  }

  const columns = [
    {
      key: 'name',
      header: 'User',
      render: (user: User) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{user.name}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
        </div>
      ),
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (user: User) => (
        <span className="text-sm text-gray-600 dark:text-gray-300">
          {user.phone || '-'}
        </span>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (user: User) => getRoleBadge(user.role),
    },
    {
      key: 'status',
      header: 'Status',
      render: (user: User) => (
        <Badge variant={user.is_active ? 'success' : 'gray'}>
          {user.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'lastLogin',
      header: 'Last Login',
      render: (user: User) => (
        <span className="text-sm text-gray-600 dark:text-gray-300">
          {user.last_login_at ? formatDate(user.last_login_at) : 'Never'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (user: User) => (
        <div className="flex justify-end gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation()
              setEditingUser(user)
            }}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
            title="Edit"
          >
            <i className="fas fa-edit"></i>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              toggleActiveMutation.mutate({ id: user.id, is_active: !user.is_active })
            }}
            className={`p-2 rounded-lg transition-colors ${
              user.is_active
                ? 'text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                : 'text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
            }`}
            title={user.is_active ? 'Deactivate' : 'Activate'}
          >
            <i className={`fas ${user.is_active ? 'fa-user-slash' : 'fa-user-check'}`}></i>
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Management</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage who can access this application
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <i className="fas fa-user-plus mr-2"></i>
          Add User
        </Button>
      </div>

      {/* Info Banner */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex gap-3">
          <i className="fas fa-info-circle text-blue-500 mt-0.5"></i>
          <div>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Access Control:</strong> Only users listed here can log in to the application.
              Add users by their email address or phone number.
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <TypeaheadSearch
              placeholder="Search by name, email, or phone..."
              value={search}
              onChange={(value) => { setSearch(value); setPage(1) }}
              onSelect={(option) => { setSearch(option.label); setPage(1) }}
              queryKey={['users', 'search']}
              queryFn={async () => {
                try {
                  const { data } = await api.get<PaginatedResponse<User>>('/users?per_page=100')
                  return (data.data || []).map((user: User): TypeaheadOption => ({
                    id: user.id,
                    label: user.name,
                    sublabel: user.email || user.phone || user.role,
                    icon: user.role === 'admin' ? 'fa-user-shield' : 'fa-user',
                  }))
                } catch {
                  return []
                }
              }}
              maxResults={8}
            />
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Users', count: data?.meta?.total ?? 0, icon: 'fa-users', color: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30' },
          { label: 'Active', count: data?.data?.filter(u => u.is_active).length ?? 0, icon: 'fa-user-check', color: 'text-green-600 bg-green-100 dark:bg-green-900/30' },
          { label: 'Inactive', count: data?.data?.filter(u => !u.is_active).length ?? 0, icon: 'fa-user-slash', color: 'text-gray-600 bg-gray-100 dark:bg-gray-700' },
          { label: 'Admins', count: data?.data?.filter(u => u.role === 'admin').length ?? 0, icon: 'fa-user-shield', color: 'text-red-600 bg-red-100 dark:bg-red-900/30' },
        ].map((stat) => (
          <Card key={stat.label} padding="sm">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}>
                <i className={`fas ${stat.icon}`}></i>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.count}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Table */}
      <Card padding="none">
        <Table
          columns={columns}
          data={data?.data ?? []}
          loading={isLoading}
          keyExtractor={(user) => user.id}
          emptyMessage="No users found. Add users to allow them to log in."
        />
        {data && data.meta.total_pages > 1 && (
          <Pagination
            page={page}
            totalPages={data.meta.total_pages}
            onPageChange={setPage}
          />
        )}
      </Card>

      {/* Create Modal */}
      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false)
          queryClient.invalidateQueries({ queryKey: ['users'] })
        }}
      />

      {/* Edit Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          isOpen={!!editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={() => {
            setEditingUser(null)
            queryClient.invalidateQueries({ queryKey: ['users'] })
          }}
        />
      )}
    </div>
  )
}

interface CreateUserModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

function CreateUserModal({ isOpen, onClose, onSuccess }: CreateUserModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'operator',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }
    
    if (!formData.email.trim() && !formData.phone.trim()) {
      newErrors.email = 'Either email or phone is required'
      newErrors.phone = 'Either email or phone is required'
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }
    
    if (formData.phone && formData.phone.length !== 10) {
      newErrors.phone = 'Phone must be 10 digits'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return api.post('/users', {
        name: data.name.trim(),
        email: data.email.trim() || undefined,
        phone: data.phone.trim() ? `+91${data.phone}` : undefined,
        role: data.role,
      })
    },
    onSuccess: () => {
      setFormData({ name: '', email: '', phone: '', role: 'technician' })
      setErrors({})
      onSuccess()
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || 'Failed to create user'
      setErrors({ form: message })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validateForm()) {
      mutation.mutate(formData)
    }
  }

  const handleClose = () => {
    setFormData({ name: '', email: '', phone: '', role: 'technician' })
    setErrors({})
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add New User" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Full Name *"
          value={formData.name}
          onChange={(e) => {
            setFormData({ ...formData, name: e.target.value })
            if (errors.name) setErrors({ ...errors, name: '' })
          }}
          error={errors.name}
          placeholder="John Doe"
        />

        <Input
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={(e) => {
            setFormData({ ...formData, email: e.target.value })
            if (errors.email) setErrors({ ...errors, email: '' })
          }}
          error={errors.email}
          placeholder="john@example.com"
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Phone Number
          </label>
          <div className="flex">
            <span className="inline-flex items-center px-3 border border-r-0 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-l-lg text-sm">
              +91
            </span>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => {
                setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })
                if (errors.phone) setErrors({ ...errors, phone: '' })
              }}
              className={`flex-1 px-4 py-2.5 border rounded-r-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                errors.phone 
                  ? 'border-red-500 dark:border-red-500' 
                  : 'border-gray-300 dark:border-gray-600'
              }`}
              placeholder="9876543210"
            />
          </div>
          {errors.phone && (
            <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
              <i className="fas fa-exclamation-circle text-xs"></i>
              {errors.phone}
            </p>
          )}
        </div>

        <SearchableSelect
          label="Role *"
          value={formData.role}
          onChange={(val) => setFormData({ ...formData, role: val })}
          searchable={false}
          options={ROLES.map(role => ({
            value: role.value,
            label: role.label,
            sublabel: role.description,
          }))}
        />

        <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <i className="fas fa-info-circle mr-2"></i>
            This user will be able to log in using the email or phone number provided.
          </p>
        </div>

        {errors.form && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
              <i className="fas fa-exclamation-circle"></i>
              {errors.form}
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            <i className="fas fa-user-plus mr-2"></i>
            Add User
          </Button>
        </div>
      </form>
    </Modal>
  )
}

interface EditUserModalProps {
  user: User
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

function EditUserModal({ user, isOpen, onClose, onSuccess }: EditUserModalProps) {
  const [formData, setFormData] = useState({
    name: user.name,
    phone: user.phone?.replace('+91', '') || '',
    role: user.role,
    is_active: user.is_active,
  })
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return api.put(`/users/${user.id}`, {
        name: data.name,
        phone: data.phone ? `+91${data.phone}` : undefined,
        role: data.role,
        is_active: data.is_active,
      })
    },
    onSuccess,
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return api.delete(`/users/${user.id}`)
    },
    onSuccess,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    mutation.mutate(formData)
  }

  const handleDelete = () => {
    deleteMutation.mutate()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit User" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <p className="text-sm text-gray-500">Email</p>
          <p className="font-medium text-gray-900 dark:text-white">{user.email}</p>
        </div>

        <Input
          label="Full Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Phone Number
          </label>
          <div className="flex">
            <span className="inline-flex items-center px-3 border border-r-0 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-l-lg text-sm">
              +91
            </span>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
              className="flex-1 px-4 py-2.5 border rounded-r-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
              placeholder="9876543210"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Phone number can be updated for login purposes</p>
        </div>

        <SearchableSelect
          label="Role"
          value={formData.role}
          onChange={(val) => setFormData({ ...formData, role: val })}
          searchable={false}
          options={ROLES.map(role => ({
            value: role.value,
            label: role.label,
          }))}
        />

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="is_active"
            checked={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <label htmlFor="is_active" className="text-sm text-gray-700 dark:text-gray-300">
            User is active and can log in
          </label>
        </div>

        {/* Suspend/Delete Section */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">User Actions</h4>
          {!showDeleteConfirm ? (
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setFormData({ ...formData, is_active: false })}
                className="text-orange-600 border-orange-300 hover:bg-orange-50"
              >
                <i className="fas fa-pause mr-2"></i>
                Suspend User
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                <i className="fas fa-trash mr-2"></i>
                Delete User
              </Button>
            </div>
          ) : (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400 mb-3">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                Are you sure you want to deactivate this user? They will no longer be able to log in.
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleDelete}
                  loading={deleteMutation.isPending}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Confirm Delete
                </Button>
              </div>
            </div>
          )}
        </div>

        {mutation.isError && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">
              Failed to update user. Please try again.
            </p>
          </div>
        )}

        {mutation.isSuccess && (
          <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-sm text-green-600 dark:text-green-400">
              <i className="fas fa-check-circle mr-2"></i>
              User updated successfully!
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            <i className="fas fa-save mr-2"></i>
            Save Changes
          </Button>
        </div>
      </form>
    </Modal>
  )
}
